import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { RootState } from '../../../core/store/store';
import { logout } from '../../../core/store/slices/userSlice';
import SessionService from '../../../shared/services/sessionService';
import useUserProfileSync from '../../../shared/hooks/useUserProfileSync';
import { useKeyboardShortcuts } from '../../../shared/hooks/useKeyboardShortcuts';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import StatsSection from './components/StatsSection';
import SupportModal from './components/SupportModal';
import SettingsModal from './components/SettingsModal';
import RankingModal from './components/RankingModal';
import ShopModal from './components/ShopModal';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import { roleLabelFromStatsT } from './roleLabel';

const Dashboard: React.FC = () => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const { t } = usePageTranslation('game', 'Dashboard');
  const { t: tHeader } = usePageTranslation('game', 'DashboardHeader');
  const { t: tStats } = usePageTranslation('game', 'StatsSection');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [now, setNow] = useState(() => new Date());
  const [tutorialEnabled, setTutorialEnabled] = useState(() => {
    return localStorage.getItem('tutorialEnabled') === 'true';
  });
  const [showSettings, setShowSettings] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showRanking, setShowRanking] = useState(false);
  const [showShop, setShowShop] = useState(false);

  useUserProfileSync(5000, true);

  useKeyboardShortcuts({
    onSettingsToggle: () => setShowSettings(!showSettings),
    onMapToggle: () => navigate('/game/map'),
    enabled: !showSettings && !showSupport && !showRanking && !showShop,
  });

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const handleLogout = async () => {
    try {
      const sessionService = SessionService.getInstance();
      await sessionService.logout();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      dispatch(logout());
      localStorage.removeItem('token');
      navigate('/');
    }
  };

  React.useEffect(() => {
    localStorage.setItem('tutorialEnabled', tutorialEnabled.toString());
  }, [tutorialEnabled]);

  const headerClock = now.toLocaleString('fr-FR', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  if (!currentUser) {
    return null;
  }

  const roleLabel = roleLabelFromStatsT(currentUser.roles_id, tStats);

  return (
    <div className="fixed inset-0 overflow-hidden bg-slate-950/92 text-slate-100">
      <BackgroundImage />

      <header className="absolute inset-x-0 top-0 z-[1200] border-b border-slate-600/45 bg-gradient-to-b from-slate-700/95 to-slate-900/95 backdrop-blur-md">
        <div className="flex min-h-14 flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2 sm:px-5">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3 sm:gap-4">
            <Link to="/" className="flex shrink-0 items-center gap-2.5">
              <img src="/images/logo.png" alt="Logo" className="h-9 w-9 rounded-md object-contain ring-1 ring-slate-500/40" />
              <div className="min-w-0 hidden sm:block">
                <p className="text-[9px] uppercase tracking-[0.22em] text-slate-300">Tableau de bord</p>
                <p className="truncate text-sm font-bold tracking-wide text-white">OPSECOURS WEB</p>
              </div>
            </Link>
            <div className="hidden items-center gap-2.5 border-l border-slate-600/60 pl-4 text-[12px] sm:flex">
              <div className="rounded-lg border border-emerald-500/35 bg-emerald-950/30 px-3 py-1.5 font-semibold text-emerald-100 shadow-inner shadow-emerald-900/20">
                € {Number(currentUser.argents ?? 0).toLocaleString('fr-FR')}
              </div>
              <div className="rounded-lg border border-amber-500/35 bg-amber-950/25 px-3 py-1.5 font-semibold text-amber-100 shadow-inner shadow-amber-900/15">
                🏅 {Number(currentUser.medailles ?? 0).toLocaleString('fr-FR')}
              </div>
            </div>
          </div>

          <div className="flex w-full max-w-full flex-none flex-wrap items-center justify-end gap-3 sm:w-auto sm:max-w-[75%] lg:max-w-[70%]">
            <div className="flex w-full max-w-full shrink-0 flex-wrap items-center justify-end gap-2 sm:w-auto sm:max-w-none sm:flex-nowrap sm:gap-2.5">
              <div
                className="flex min-w-0 max-w-full flex-1 justify-end sm:flex-initial sm:max-w-[min(100%,280px)] lg:max-w-[320px]"
                aria-label={t('header.clockHint')}
              >
                <time
                  dateTime={now.toISOString()}
                  className="w-full truncate rounded-xl border border-slate-600/50 bg-slate-900/60 px-3 py-2 text-center text-[10px] font-medium leading-snug text-slate-200 sm:w-auto sm:text-[11px]"
                  title={t('header.clockHint')}
                >
                  {headerClock}
                </time>
              </div>

              {currentUser.roles_id != null && currentUser.roles_id <= 5 && (
                <nav className="flex shrink-0 items-center justify-end" aria-label={t('header.navPanel')}>
                  <button
                    type="button"
                    onClick={() => navigate('/panel')}
                    className="flex items-center gap-2 rounded-xl border border-blue-500/40 bg-blue-950/25 px-3 py-2 text-sm font-medium text-blue-100 transition hover:bg-blue-900/35"
                  >
                    <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                      />
                    </svg>
                    {tHeader('panel')}
                  </button>
                </nav>
              )}

              <nav
                className="flex shrink-0 flex-wrap items-center justify-end gap-2"
                aria-label={t('header.navAssistance')}
              >
                <button
                  type="button"
                  onClick={() => setShowSupport(true)}
                  className="rounded-xl border border-slate-500/50 bg-slate-800/60 px-3 py-2 text-sm font-medium text-slate-100 transition hover:bg-slate-700/80"
                >
                  {tHeader('support')}
                </button>
              </nav>
            </div>

            <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto sm:gap-3">

              <span className="hidden h-8 w-px shrink-0 bg-slate-600/55 sm:inline" aria-hidden />

              <nav
                className="flex flex-wrap items-center justify-end gap-2 sm:gap-2.5"
                aria-label={t('header.navGameExtras')}
              >
                <button
                  type="button"
                  onClick={() => setShowRanking(true)}
                  className="rounded-xl border border-amber-500/40 bg-amber-950/20 px-3 py-2 text-sm font-medium text-amber-100 transition hover:bg-amber-900/35"
                >
                  {tHeader('ranking')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowShop(true)}
                  className="rounded-xl border border-green-500/40 bg-green-950/20 px-3 py-2 text-sm font-medium text-green-100 transition hover:bg-green-900/35"
                >
                  {tHeader('shop')}
                </button>
              </nav>

              {currentUser.roles_id === 1 && (
                <>
                  <span className="hidden h-8 w-px shrink-0 bg-slate-600/55 sm:inline" aria-hidden />
                  <nav className="flex flex-wrap items-center justify-end" aria-label={t('header.navAllianceAdmin')}>
                    <button
                      type="button"
                      onClick={() => navigate('/dashboard/alliance')}
                      className="rounded-xl border border-indigo-500/45 bg-indigo-950/25 px-3 py-2 text-sm font-medium text-indigo-100 transition hover:bg-indigo-900/35"
                    >
                      {tHeader('alliance')}
                    </button>
                  </nav>
                </>
              )}

              <span className="hidden h-8 w-px shrink-0 bg-slate-600/55 md:inline" aria-hidden />

              <nav className="flex flex-wrap items-center justify-end gap-2 sm:gap-2.5" aria-label={t('header.sessionNav')}>
                <button
                  type="button"
                  onClick={() => setShowSettings(true)}
                  className="rounded-xl border border-slate-500/50 bg-slate-800/60 px-3 py-2 text-sm font-medium text-slate-100 transition hover:bg-slate-700/80"
                >
                  {tHeader('settings')}
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="rounded-xl border border-red-500/40 bg-red-950/25 px-3 py-2 text-sm font-medium text-red-100 transition hover:bg-red-900/35"
                >
                  {tHeader('logout')}
                </button>
              </nav>

              <div className="ml-0 hidden max-w-[160px] truncate rounded-xl border border-emerald-500/35 bg-emerald-500/10 px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-emerald-100 sm:ml-1 sm:inline lg:max-w-[200px]">
                {currentUser.username}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4" style={{ top: '72px' }}>
        <div className="mx-auto grid h-full min-h-0 max-w-[1680px] grid-cols-1 gap-5 lg:grid-cols-[minmax(300px,400px),minmax(0,1fr)] xl:gap-8">
          <section className="relative flex min-h-[240px] flex-col overflow-hidden rounded-2xl border border-slate-600/40 bg-slate-900/45 shadow-2xl shadow-black/40 backdrop-blur-md lg:min-h-0">
            <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-500/12 via-transparent to-violet-600/12" />
            <div className="relative border-b border-slate-600/35 bg-slate-950/55 px-4 py-4 sm:px-6">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-400/90">
                {t('welcomePanel.tagline')}
              </p>
              <p className="mt-1 text-[11px] font-medium text-slate-500">{t('welcomePanel.taglineSub')}</p>
              <h2 className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl">
                {t('welcome')},{' '}
                <span className="bg-gradient-to-r from-emerald-300 to-teal-200 bg-clip-text text-transparent">
                  {currentUser.username}
                </span>
              </h2>
            </div>

            <div className="relative flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:gap-5 sm:p-6">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-slate-700/55 bg-slate-950/55 p-3.5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{t('welcomePanel.idLabel')}</p>
                  <p className="mt-1.5 font-mono text-lg font-semibold text-white">#{currentUser.id}</p>
                </div>
                <div className="rounded-xl border border-slate-700/55 bg-slate-950/55 p-3.5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{t('welcomePanel.roleLabel')}</p>
                  <p className="mt-1.5 text-lg font-semibold text-violet-200">{roleLabel}</p>
                </div>
              </div>
              <div className="rounded-xl border border-slate-700/55 bg-slate-950/55 p-3.5">
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{t('welcomePanel.emailLabel')}</p>
                <p className="mt-1.5 truncate text-sm font-medium text-slate-100">{currentUser.email}</p>
              </div>
              {(currentUser.department || currentUser.departement_code) && (
                <div className="rounded-xl border border-slate-700/55 bg-slate-950/55 p-3.5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{t('welcomePanel.departmentLabel')}</p>
                  <p className="mt-1.5 text-lg font-semibold text-slate-100">
                    {currentUser.department ?? String(currentUser.departement_code ?? '')}
                  </p>
                </div>
              )}

              <div className="rounded-xl border border-sky-500/30 bg-sky-950/20 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-sky-400/90">{t('welcomePanel.tutorialBlockTitle')}</p>
                    <p className="mt-1.5 text-sm leading-snug text-slate-300">{t('welcomePanel.tutorialBlockHint')}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setTutorialEnabled((v) => !v)}
                    title={tutorialEnabled ? t('welcomePanel.tutorialTitleOn') : t('welcomePanel.tutorialTitleOff')}
                    aria-pressed={tutorialEnabled}
                    className={`shrink-0 rounded-xl border px-4 py-2.5 text-sm font-semibold transition sm:self-center ${
                      tutorialEnabled
                        ? 'border-sky-400/60 bg-sky-600/30 text-sky-50 hover:bg-sky-600/40'
                        : 'border-slate-500/60 bg-slate-800/80 text-slate-100 hover:bg-slate-700/90'
                    }`}
                  >
                    {tutorialEnabled ? t('welcomePanel.tutorialStateOn') : t('welcomePanel.tutorialStateOff')}
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={() => navigate('/game/map')}
                title={t('header.mapCtaTitle')}
                className="w-full rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-3.5 text-center shadow-lg shadow-emerald-900/25 transition hover:from-emerald-500 hover:to-teal-500 sm:py-4"
              >
                <span className="block text-sm font-bold uppercase tracking-wide text-white">{t('header.mapCta')}</span>
              </button>

              <div className="mt-auto flex flex-wrap gap-2 border-t border-slate-700/45 pt-4 sm:hidden">
                <div className="rounded-lg border border-emerald-500/40 bg-emerald-900/25 px-2 py-1 text-[11px] font-semibold text-emerald-100">
                  € {Number(currentUser.argents ?? 0).toLocaleString('fr-FR')}
                </div>
                <div className="rounded-lg border border-amber-500/40 bg-amber-900/20 px-2 py-1 text-[11px] font-semibold text-amber-100">
                  🏅 {Number(currentUser.medailles ?? 0).toLocaleString('fr-FR')}
                </div>
              </div>
            </div>
          </section>

          <section className="relative flex min-h-[320px] flex-col overflow-hidden rounded-2xl border border-slate-600/40 bg-slate-900/45 shadow-2xl shadow-black/40 backdrop-blur-md lg:min-h-0">
            <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-sky-500/10 via-transparent to-slate-950/85" />
            <div className="relative border-b border-slate-600/35 bg-slate-950/55 px-4 py-4 sm:px-6">
              <h2 className="text-xl font-bold text-white sm:text-2xl">{t('statsPanel.title')}</h2>
              <p className="mt-1 text-sm text-slate-400">{t('statsPanel.subtitle')}</p>
            </div>
            <div className="relative min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-5 sm:py-5">
              <StatsSection currentUser={currentUser} />
            </div>
          </section>
        </div>
      </div>

      <SupportModal isOpen={showSupport} onClose={() => setShowSupport(false)} />

      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        currentUser={currentUser}
      />

      <RankingModal isOpen={showRanking} onClose={() => setShowRanking(false)} />

      <ShopModal isOpen={showShop} onClose={() => setShowShop(false)} />
    </div>
  );
};

export default Dashboard;
